/*
 * @Description: app actions
 * @Author: ZY
 * @Date: 2020-12-23 10:25:37
 * @LastEditors: fugang
 * @LastEditTime: 2022-05-11 11:55:45
 */
import { ActionTree, ActionContext } from "vuex";
import { RootState, useStore } from "@/store";
import { UserState } from "./state";
import { Mutations } from "./mutations";
import { UserMutationTypes } from "./mutation-types";
import { UserActionTypes } from "./action-types";
import { api } from "@/apis/user";
import {
  setSession,
  setToken,
  setLoginName,
  setPerrsion,
  getLoginName,
  getToken,
  getSession,
  setLoginId,
  setLoginUid,
  setSystemPermission,
  setSessionSecretKey,
  clearAuthStorage
} from "@/utils/cookies";
import { resolveSystemPermission } from "@/utils/resolve-system-permission";
import { PermissionActionType } from "../permission/action-types";
import { resetRouter } from "@/router";

type AugmentedActionContext = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<UserState, RootState>, "commit">;

export interface Actions {
  [UserActionTypes.ACTION_LOGIN](
    { commit }: AugmentedActionContext,
    userInfo: { loginName: string; password: string }
  ): Promise<boolean>;
  [UserActionTypes.ACTION_RESET_TOKEN]({
    commit
  }: AugmentedActionContext): void;
  [UserActionTypes.ACTION_GET_USER_INFO]({
    commit,
    state
  }: AugmentedActionContext): void;
  [UserActionTypes.ACTION_CHANGE_ROLES](
    { commit }: AugmentedActionContext,
    role: string
  ): void;
  [UserActionTypes.ACTION_LOGIN_OUT]({ commit }: AugmentedActionContext): void;
}

export const actions: ActionTree<UserState, RootState> & Actions = {
  async [UserActionTypes.ACTION_LOGIN](
    { commit }: AugmentedActionContext,
    userInfo: { loginName: string; password: string }
  ): Promise<boolean> {
    let { loginName, password } = userInfo;
    loginName = loginName.trim();
    try {
      const res: any = await api.login.loginRequest({ loginName, password });
      const loginData = res?.data?.data;
      const businessStatus = Number(res?.data?.status ?? 0);
      if (
        res.status !== 200 ||
        businessStatus !== 200 ||
        !loginData?.token
      ) {
        return false;
      }

      setToken(loginData.token);
      setLoginName(loginData.loginUser?.loginName ?? loginName);
      setLoginId(String(loginData.loginUser?.id ?? ""));
      if (loginData.loginUser?.uid != null) {
        setLoginUid(loginData.loginUser.uid);
      }
      setSession(loginData["manager-web-session"] ?? "");
      setSessionSecretKey(loginData.sessionSecretKey ?? "");
      setSystemPermission(resolveSystemPermission(loginData.loginUser));

      commit(UserMutationTypes.SET_TOKEN, loginData.token);
      commit(
        UserMutationTypes.SET_SESSION,
        loginData["manager-web-session"] ?? ""
      );
      commit(UserMutationTypes.SET_ROLES, []);

      const store = useStore();
      await store.dispatch(PermissionActionType.ACTION_GENERATE_ROUTES, undefined);

      try {
        const detailRes: any = await api.table.rolefinddetail({
          adminId: loginData.loginUser?.id
        });
        if (detailRes?.data?.data) {
          setPerrsion(JSON.stringify(detailRes.data.data));
        }
      } catch {
        // 权限详情获取失败不阻断登录
      }

      return true;
    } catch {
      return false;
    }
  },

  [UserActionTypes.ACTION_RESET_TOKEN]({ commit }: AugmentedActionContext) {
    clearAuthStorage();
    commit(UserMutationTypes.SET_TOKEN, "");
    commit(UserMutationTypes.SET_ROLES, []);
    commit(UserMutationTypes.SET_NAME, "");
    useStore().dispatch(PermissionActionType.ACTION_RESET_ROUTES, undefined);
    resetRouter();
  },

  async [UserActionTypes.ACTION_GET_USER_INFO]({
    commit,
    state: userState
  }: AugmentedActionContext) {
    const token = getToken() || userState.token;
    if (!token) {
      throw new Error("token is undefined!");
    }
    if (userState.token !== token) {
      commit(UserMutationTypes.SET_TOKEN, token);
    }
    const session = getSession() || userState.managerWebSession;
    if (session && userState.managerWebSession !== session) {
      commit(UserMutationTypes.SET_SESSION, session);
    }

    const rolesname: string[] = [];
    const roleName = localStorage.getItem("systempremmsion");
    if (roleName) {
      rolesname.push(roleName);
    }
    commit(UserMutationTypes.SET_ROLES, rolesname);
    commit(
      UserMutationTypes.SET_NAME,
      String(getLoginName() || "").trim()
    );
    commit(
      UserMutationTypes.SET_AVATAR,
      "https://wpimg.wallstcn.com/f778738c-e4f8-4870-b634-56703b4acafe.gif"
    );
    commit(UserMutationTypes.SET_INTRODUCTION, "I am a super administrator");
    commit(UserMutationTypes.SET_EMAIL, "2218889604@qq.com");
  },

  async [UserActionTypes.ACTION_CHANGE_ROLES](
    { commit }: AugmentedActionContext,
    role: string
  ) {
    const token = role + "-token";
    const store = useStore();
    commit(UserMutationTypes.SET_TOKEN, token);
    setToken(token);
    await store.dispatch(PermissionActionType.ACTION_GENERATE_ROUTES, undefined);
  },

  [UserActionTypes.ACTION_LOGIN_OUT]({ commit }: AugmentedActionContext) {
    clearAuthStorage();
    commit(UserMutationTypes.SET_TOKEN, "");
    commit(UserMutationTypes.SET_ROLES, []);
    commit(UserMutationTypes.SET_NAME, "");
    useStore().dispatch(PermissionActionType.ACTION_RESET_ROUTES, undefined);
    resetRouter();
  }
};
